import { useCallback, useState } from 'react';

export type UseControlledStateProps<T = unknown> = {
  value?: T;
  defaultValue?: T;
  onChange?: (value: T) => void;
};

export function useControlledState<T = unknown>({
  value,
  defaultValue,
  onChange = () => {},
}: UseControlledStateProps<T>): [T, React.Dispatch<React.SetStateAction<T>>] {
  const [internal, setInternal] = useState<T>(defaultValue as T);

  const isControlled = typeof value === 'boolean';
  const returnValue = isControlled ? value : internal;

  const setValue = useCallback<React.Dispatch<React.SetStateAction<T>>>(
    (_value) => {
      const newValue =
        typeof _value === 'function'
          ? (_value as (o: T) => T)(returnValue)
          : _value;

      onChange(newValue);

      if (!isControlled) {
        setInternal(newValue);
      }
    },
    [isControlled, onChange, returnValue]
  );

  return [returnValue, setValue];
}
